/**
 * The whole drinks card (docs/17-getraenke.md, Die Getränkekarte; F18 block 3): prepared drinks
 * (Spritz, Gin Tonic, Schorle) as master data of their own, the owner's rubrics across profiles,
 * the order of the parts, own headings, the footnotes for additives and the check of the rule for
 * alcohol-free drinks. Only data; drinks-card.ts builds the card, the app lays it out.
 */
import { ADDITIVES, isAlcoholFree } from "./drinks.ts";
import type { Entity } from "./entities.ts";
import type { GlassPrice } from "./glass.ts";
import type { Id } from "./model.ts";

/**
 * Kinds of prepared drinks. Aperitif, Longdrink and Cocktail have parts of their own; a juice
 * spritzer (Apfelschorle) and a Mocktail stand with the alcohol-free drinks; a wine spritzer
 * (Rieslingschorle, 6 to 7 % vol.) with the open wines (Martin, 18.10.2026).
 */
export const RECIPE_KINDS = ["Aperitif", "Longdrink", "Cocktail", "Saftschorle", "Weinschorle", "Mocktail", "Sonstiges"] as const;

/** Section titles of prepared drinks in the parts of others: „Schorle“ next to bottled ones, „Mocktails“, „Weinschorlen“. */
export const RECIPE_SECTION: Readonly<Record<string, string>> = { Saftschorle: "Schorle", Schorle: "Schorle", Mocktail: "Mocktails", Weinschorle: "Weinschorlen" };

/** Alcohol-free by its kind: a juice spritzer and a Mocktail („Schorle“ from before is taken as one). */
export const freeKind = (kind: string) => kind === "Saftschorle" || kind === "Mocktail" || kind === "Schorle";

/** A part of a prepared drink: an article and how much of it (4 cl Gin, 0,2 l Tonic). Booked with block stage 2, not yet. */
export interface Component {
  article: Id;
  ml: number;
}

/** A prepared drink (Rezeptur, entity type `recipe`): on the card, but no stock of its own. */
export interface Recipe {
  id: Id;
  name: string;
  kind: string;
  glasses: GlassPrice[];
  cardText: string | null;
  listNo: string | null;
  offCard: boolean;
  rubric: string | null;
  additives: string[];
  allergens: string[];
  /** Ticked „enthält keine der 14“ (F29): without it a drink without allergens is not kept up. */
  allergenFree?: boolean;
  components: Component[];
}

const strs = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
const str = (v: unknown) => (typeof v === "string" && v.trim() !== "" ? v.trim() : null);

export function recipeOf(e: Entity): Recipe | null {
  const f = e.fields;
  if (e.type !== "recipe" || typeof f.name !== "string" || f.archived === true) return null;
  return {
    id: e.id,
    name: f.name,
    kind: typeof f.kind === "string" ? f.kind : "Sonstiges",
    glasses: Array.isArray(f.glasses) ? (f.glasses as GlassPrice[]).filter((g) => typeof g?.ml === "number" && typeof g?.price === "number") : [],
    cardText: str(f.cardText),
    listNo: str(f.listNo),
    offCard: f.offCard === true,
    rubric: str(f.rubric),
    additives: strs(f.additives),
    allergens: strs(f.allergens),
    allergenFree: f.allergenFree === true,
    components: Array.isArray(f.components) ? (f.components as Component[]).filter((c) => typeof c?.article === "string" && typeof c?.ml === "number") : [],
  };
}

/** A rubric of the card: an own part across profiles („Aperitif“ with Sekt, Spritz and Campari). */
export interface Rubric {
  id: string;
  title: string;
}

/** The rubrics a card starts with; the owner renames, adds and removes them (master data `card` „rubrics“). */
export const DEFAULT_RUBRICS: readonly Rubric[] = [
  { id: "aperitif", title: "Aperitif" },
  { id: "longdrinks", title: "Longdrinks und Cocktails" },
  { id: "digestif", title: "Digestif" },
];

/** Where a prepared drink without a rubric goes, by its kind. */
export function recipePlace(r: Recipe): string {
  if (r.rubric) return `rubric:${r.rubric}`;
  if (r.kind === "Aperitif") return "rubric:aperitif";
  if (freeKind(r.kind)) return "alkoholfrei";
  if (r.kind === "Weinschorle") return "offen";
  return "rubric:longdrinks";
}

/** The parts of the card in the order they start with (docs/17-getraenke.md, Reihenfolge als Voreinstellung). */
export const DEFAULT_ORDER: readonly string[] = ["rubric:aperitif", "alkoholfrei", "bier", "weitere", "offen", "flasche", "rubric:longdrinks", "spirituosen", "rubric:digestif"];

/** The titles of the parts that are not rubrics. */
export const PART_TITLES: Readonly<Record<string, string>> = {
  alkoholfrei: "Alkoholfreie Getränke",
  bier: "Biere",
  weitere: "Weitere Getränke",
  offen: "Offene Weine",
  flasche: "Flaschenweine",
  spirituosen: "Spirituosen",
};

/** The order of a card: the owner's, then whatever part is new (a rubric added later) in the default place or at the end. */
export function partOrder(own: readonly string[] | undefined, rubrics: readonly Rubric[]): string[] {
  const all = [...DEFAULT_ORDER.filter((k) => !k.startsWith("rubric:") || rubrics.some((r) => `rubric:${r.id}` === k)), ...rubrics.map((r) => `rubric:${r.id}`).filter((k) => !DEFAULT_ORDER.includes(k))];
  const start = (own ?? []).filter((k) => all.includes(k));
  return [...start, ...all.filter((k) => !start.includes(k))];
}

/** The footnote number of an additive: fixed by the list, so „8“ always means koffeinhaltig. */
export const additiveNumber = (a: string) => ADDITIVES.indexOf(a as (typeof ADDITIVES)[number]) + 1;

/** Headings as typed: „Sekt = Sekt & Champagner“, one per line. */
export function parseHeadings(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split("\n")) {
    const m = /^(.+?)\s*=\s*(.+)$/.exec(line.trim());
    if (m) out[m[1]!.trim()] = m[2]!.trim();
  }
  return out;
}

export const formatHeadings = (h: Readonly<Record<string, string>>) =>
  Object.entries(h)
    .map(([k, v]) => `${k} = ${v}`)
    .join("\n");

/** Whether a drink of these fields is alcohol-free (profile Alkoholfrei, or at most 0,5 % vol.). */
export const freeOfAlcohol = (f: Record<string, unknown>) => f.profile === "alkoholfrei" || isAlcoholFree(f.alcohol);
